import type { Dokument } from "../components/DokumentLightbox";

export type InvoiceKind = "quote" | "invoice" | "partial_invoice" | "final_invoice";
export type InvoiceStatus =
  | "entwurf"
  | "gestellt"
  | "teilbezahlt"
  | "bezahlt"
  | "ueberfaellig"
  | "storniert"
  | "angenommen"
  | "abgelehnt";

export type InvoiceActivity = {
  ts?: string;
  typ?: string;
  text?: string;
  autor?: string;
};

export type InvoiceRegisterItem = {
  id: string;
  nummer: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  projekt_id: string;
  projekt_title: string;
  kunde_id: string;
  kunde_name: string;
  issue_date: string;
  leistungsdatum: string;
  faellig_am: string;
  netto: number;
  mwst: number;
  brutto: number;
  waehrung: string;
  source_quote_nummer: string;
  yaml_pfad: string;
  pdf_pfad: string;
  xml_pfad: string;
  pdf_sha256: string;
  erzeugt_via: string;
  web_pdf_pfad: string;
  web_xml_pfad: string;
  web_pdf_sha256: string;
  web_festgeschrieben_am: string;
  web_pdf_available: boolean;
  bezahlt_am: string;
  kontobewegung_id: string;
  notiz: string;
  aktivitaet: InvoiceActivity[];
  created_at: string;
  updated_at: string;
  kb_datum: string;
  kb_betrag: number;
  kb_gegenpartei: string;
  /** Wann der Beleg an DATEV ging; "" = noch nicht gesendet (nur Rechnungen). */
  an_steuerberater_versendet_at: string;
  /** Nur bei Angeboten: Positionsquelle für prozentuale Abschläge. */
  web_document_json?: WebInvoiceDraft | null;
  /** Rechnung schließt den Auftrag ab, Rest aus dem Angebot entfällt (Migration 216). */
  schliesst_auftrag?: boolean;
};

export type InvoiceSummary = {
  open_claims: number;
  open_count: number;
  overdue_claims: number;
  overdue_count: number;
  offers_outside: number;
  offers_count: number;
  paid_total: number;
  paid_count: number;
};

export type WebInvoiceLineKind = "position" | "optional" | "section";

export type WebInvoiceLine = {
  id: string;
  line_kind: WebInvoiceLineKind;
  title: string;
  description: string;
  quantity: number;
  unit_label: string;
  unit_code: string;
  unit_price_net: number;
  vat_rate_percent: 0 | 7 | 19;
  /** Nur intern, nie im PDF: Einkaufspreis und Aufschlag einer Pflanze aus der Bibliothek. */
  einkauf_netto?: number | null;
  aufschlag?: number | null;
  pflanze_id?: string | null;
};

/**
 * Bereits gestellte Abschlagsrechnung, die eine Schlussrechnung absetzt.
 *
 * § 14 Abs. 5 UStG verlangt, dass Teilentgelt und darauf entfallende Steuer
 * einzeln abgesetzt werden — deshalb strukturiert und nicht als Freitext.
 */
export type AbschlagAbsetzung = {
  nummer: string;
  datum: string | null;
  netto: number;
  mwst: number;
  brutto: number;
};

/**
 * Ein Beleg samt gespeichertem Dokument — die Vorlage zum Bearbeiten.
 *
 * `document` ist null, wenn Kontor den Beleg nicht selbst geschrieben hat
 * (Desktop-App, anderes Programm): dann gibt es nichts neu zu erzeugen.
 * Die übrigen Felder sagen, ob der Beleg schon nach außen wirkt.
 */
export type InvoiceDocument = {
  id: string;
  nummer: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  kunde_name: string;
  projekt_title: string;
  bezahlt_am: string | null;
  kontobewegung_id: string | null;
  aktivitaet: InvoiceActivity[];
  erzeugt_via: string;
  web_festgeschrieben_am: string | null;
  web_document_json: WebInvoiceDraft | null;
};

/**
 * Was der Server über eine von Hand eingetippte Belegnummer sagt.
 *
 * `frei` heißt „benutzbar", nicht „reserviert" — vergeben wird erst beim
 * Festschreiben. `art` trennt die Fälle, damit die Oberfläche eine harmlose
 * Registerzeile (wird ersetzt) anders färben kann als einen festgeschriebenen
 * Beleg (geht nicht).
 */
export type NummerPruefung = {
  nummer: string;
  art: "leer" | "frei" | "registerzeile" | "festgeschrieben" | "fremdes_praefix" | "kein_format";
  frei: boolean;
  vorschlag: string;
  hinweis: string;
};

export type WebInvoiceDraft = {
  kind: InvoiceKind;
  doc_number: string;
  /**
   * Darf `doc_number` beim Festschreiben beansprucht werden?
   *
   * Normalerweise nicht: jeder Entwurf trägt nur die Vorschau der nächsten
   * freien Nummer. Zwei Fälle setzen das Flag — ein KI-importierter Beleg,
   * der die echte Nummer vom Papier mitbringt, und die Handvergabe im Editor
   * (nötig, solange Lexware aus einer eigenen Reihe zieht).
   */
  nummer_uebernehmen?: boolean;
  /**
   * Die Titelzeile des fertigen Dokuments — vollständig vom Nutzer bestimmt.
   * OHNE Belegnummer: die steht oben rechts im Metablock des PDFs.
   */
  doc_title: string;
  /** ALTBESTAND: früher der Mittelteil des Titels; wird nicht mehr geschrieben. */
  subject: string;
  discount_percent: number | null;
  discount_amount?: number | null;
  discount_label?: string;
  issue_date: string;
  service_date: string | null;
  due_date: string | null;
  validity_until: string | null;
  payment_term_days: number | null;
  source_quote_number: string;
  customer_lines: string;
  customer_number: string;
  intro_paragraphs: string;
  notes_after_table: string;
  payment_reference: string;
  show_tax_quote: boolean;
  line_rows: WebInvoiceLine[];
  source_project_id: string;
  zugferd: boolean;
  xml_out: boolean;
  validate_xsd: boolean;
  abschlag_absetzungen: AbschlagAbsetzung[];
  /** Interne Kostenschätzung — steht NIE im PDF, nur im Entwurf. */
  interne_kalk?: InterneKalkulation | null;
};

/** Grobe Kostenschätzung, damit die Marge ohne Mehraufwand sichtbar wird. */
export type InterneKalkulation = {
  materialkosten: number;
  lohn_helfer: number;
  lohn_inhaber: number;
  fahrt_sonstiges: number;
};

export type WebInvoiceDraftRecord = {
  id: string;
  kind: InvoiceKind;
  kunde_id: string;
  kunde_name: string;
  projekt_id: string;
  projekt_title: string;
  document: WebInvoiceDraft;
  netto: number;
  mwst: number;
  brutto: number;
  created_by: string;
  created_at: string;
  updated_at: string;
};

export type RegisterFilter = "all" | "quotes" | "invoices" | "draft" | "open" | "overdue" | "imported";

/** Eine Seite des Registers — Kennzahlen und Reiterzähler gelten für den ganzen Bestand. */
export type InvoiceRegister = {
  items: InvoiceRegisterItem[];
  total: number;
  offset: number;
  counts: Record<RegisterFilter, number>;
  summary: InvoiceSummary;
  /** Nur mit der ersten Seite gefüllt. */
  drafts: WebInvoiceDraftRecord[];
};

export type RegisterAbfrage = {
  q?: string;
  filter?: RegisterFilter;
  kunde?: string;
  projekt?: string;
};

export type TextSnippet = { label: string; text: string };

/**
 * Die vier Bausteinlisten: Einleitung oder Schlusstext, Angebot oder Rechnung.
 *
 * Der Name steht so auch im Backend (`KontorTextbaustein.liste`) — wer hier
 * etwas ergänzt, muss dort dieselbe Schreibweise nachziehen.
 */
export type SnippetListe = "intro_invoice" | "intro_quote" | "notes_invoice" | "notes_quote";

export type InvoiceEditorOptions = {
  snippets: {
    intro_invoice: TextSnippet[];
    intro_quote: TextSnippet[];
    notes_invoice: TextSnippet[];
    notes_quote: TextSnippet[];
    defaults: {
      intro_invoice_label: string;
      intro_quote_label: string;
      notes_invoice_label: string;
      notes_quote_label: string;
    };
  };
  units: string[];
};

export type CatalogPosition = {
  title: string;
  description: string;
  unit_label: string;
  unit_code: string;
  unit_price_net: number | string;
  vat_rate_percent: number | string;
  /** Nur bei Treffern aus der Pflanzenbibliothek. */
  einkauf_netto?: number | null;
  aufschlag?: number | null;
  pflanze_id?: string | null;
};

/**
 * Ein Baustein der Bibliothek: Überschrift plus die Positionen darunter.
 *
 * „Terrasse Naturstein" ist selten eine Position und fast immer dieselben
 * sieben. Ein Blocktreffer fügt Überschrift und alle Positionen auf einmal
 * ein — im Beleg entsteht daraus ein gewöhnlicher Block.
 */
export type CatalogBlock = {
  title: string;
  description: string;
  lines: CatalogPosition[];
};

/** Woraus ein neuer Entwurf abgeleitet wird — siehe `draft_from_invoice`. */
export type DraftFromInvoiceModus = "kopie" | "rechnung" | "abschlag" | "schluss" | "storno" | "bestaetigung";

/**
 * Was aus einem eingelesenen Papier geworden ist.
 *
 * `warnungen` ist der wichtigste Teil: dort steht, was die KI nicht sicher
 * lesen konnte — allen voran die Rechenprobe zwischen den erkannten Positionen
 * und der Endsumme auf dem Beleg. Ein Import ohne Warnungen ist geprüft, einer
 * mit Warnungen ist eine Vorlage.
 */
export type InvoiceImportResult = WebInvoiceDraftRecord & {
  warnungen: string[];
  seiten: number;
  quelldatei: string;
  nummer: string;
  nummer_status: {
    belegt: boolean;
    ersetzbar: boolean;
    hinweis: string;
  };
};

export type DraftFromInvoiceResult = WebInvoiceDraftRecord & {
  modus: DraftFromInvoiceModus;
  quelle_nummer: string;
  /** false = nur die Registersumme, weil der Beleg im Desktop entstand. */
  positionen_uebernommen: boolean;
  anteile_prozent?: number[];
};

/**
 * Was das Festschreiben mit dem Projekt gemacht hat.
 *
 * Ohne diese Rueckmeldung aendert sich der Auftragswert eines Projekts still im
 * Hintergrund — der Nutzer soll sehen, dass sein Beleg gewirkt hat.
 */
export type ProjektWirkung = {
  projekt_id: string;
  auftragswert: number;
  herkunft_text: string;
  alt: number;
  neu: number;
  geaendert: boolean;
  gestellt: number;
  bezahlt: number;
  offen: number;
  nicht_abgerechnet: number;
  status_aktuell: string;
  status_vorschlag: string;
  status_grund: string;
  abgeschlossen_durch_vermerk?: boolean;
};

/** Ein Angebot, das beim Kunden liegt (rechnung_folgen.angebote_offen). */
export type OffenesAngebot = { id: string; nummer: string; netto: number; datum: string };

/**
 * Was eine festgeschriebene Rechnung am Projekt von selbst erledigt hat
 * (rechnung_folgen, Benjamin 15.10.2026): Status auf „rechnung", Quellangebot
 * angenommen — und welche Angebote ohne Bezug noch offen sind (danach fragt
 * der Fertig-Dialog). Bei Angeboten `null`.
 */
export type RechnungFolgen = {
  status_gesetzt: string;
  angebot_angenommen: string;
  angebote_offen: OffenesAngebot[];
};

export type InvoiceFinalizeResult = {
  id: string;
  number: string;
  pdf_url: string;
  item: InvoiceRegisterItem;
  projekt_wirkung: ProjektWirkung | null;
  folgen?: RechnungFolgen | null;
};

/**
 * Registereintrag mit dem, was er beim Anlegen am Projekt bewirkt hat.
 *
 * Ein importiertes Angebot sagt denselben Auftragswert zu wie ein im Kontor
 * geschriebenes — der Server rechnet ihn beim Anlegen nach und sagt hier, was
 * sich dadurch geändert hat.
 */
export type InvoiceCreated = InvoiceRegisterItem & {
  projekt_wirkung?: ProjektWirkung | null;
};

export type InvoiceCreate = {
  nummer: string;
  kind: InvoiceKind;
  status: InvoiceStatus;
  kunde_id: string;
  projekt_id: string;
  issue_date: string;
  leistungsdatum?: string | null;
  faellig_am?: string | null;
  netto: number;
  brutto: number;
  notiz: string;
};

export type InvoicePatch = Partial<Pick<
  InvoiceRegisterItem,
  "status" | "kind" | "kunde_id" | "projekt_id" | "faellig_am" | "notiz" | "schliesst_auftrag"
>>;

export const KIND_LABEL: Record<InvoiceKind, string> = {
  quote: "Angebot",
  invoice: "Rechnung",
  partial_invoice: "Abschlagsrechnung",
  final_invoice: "Schlussrechnung",
};

/**
 * Angebotsbestätigung (01.10.2026): ein Angebot, das auf ein anderes Angebot
 * verweist. Bewusst keine eigene Belegart — siehe `ist_bestaetigung` im Server.
 */
export const istAngebotsbestaetigung = (kind: InvoiceKind, quelleAngebot?: string | null) => (
  kind === "quote" && !!(quelleAngebot || "").trim()
);

/** Was das Bestätigen einer Angebotsbestätigung sonst noch umgestellt hat. */
export type BestaetigungFolgen = {
  angebot: string;
  angebot_angenommen: boolean;
  projekt_aktiv: boolean;
};

/** Belegart zum Anzeigen — „Angebotsbestätigung" statt „Angebot", wo es eine ist. */
export const belegArtLabel = (kind: InvoiceKind, quelleAngebot?: string | null) => (
  istAngebotsbestaetigung(kind, quelleAngebot) ? "Angebotsbestätigung" : KIND_LABEL[kind]
);

/**
 * Belegarten, die sich am festgeschriebenen Beleg noch umstellen lassen.
 *
 * Ohne `quote`: ob ein Papier Angebot oder Rechnung ist, entscheidet der
 * Nummernkreis, aus dem seine Nummer stammt — das ist nicht rückwirkend zu
 * drehen. Abschlag ⇄ Rechnung ⇄ Schlussrechnung ist dagegen eine reine
 * Einordnung desselben Papiers, und sie entscheidet über den Auftragswert des
 * Projekts. Der Server prüft dieselbe Grenze (`RechnungsArt`).
 */
export const RECHNUNGS_ARTEN: InvoiceKind[] = ["invoice", "partial_invoice", "final_invoice"];

export const KIND_HINT: Record<InvoiceKind, string> = {
  quote: "Ein Angebot — es sagt den Auftragswert zu.",
  invoice: "Rechnet den Auftrag ab. Der Auftragswert des Projekts folgt ab jetzt den Rechnungen.",
  partial_invoice: "Fordert einen Teilbetrag. Der zugesagte Auftragswert bleibt stehen, bis die Schlussrechnung kommt.",
  final_invoice: "Beendet den Auftrag. Der Auftragswert wird endgültig auf die Summe aller Rechnungen festgeschrieben.",
};

export const STATUS_LABEL: Record<InvoiceStatus, string> = {
  entwurf: "Entwurf",
  gestellt: "Gestellt",
  teilbezahlt: "Teilbezahlt",
  bezahlt: "Bezahlt",
  ueberfaellig: "Überfällig",
  storniert: "Storniert",
  angenommen: "Angenommen",
  abgelehnt: "Abgelehnt",
};

export const allowedStatuses = (kind: InvoiceKind): InvoiceStatus[] => kind === "quote"
  ? ["entwurf", "gestellt", "angenommen", "abgelehnt", "storniert"]
  : ["entwurf", "gestellt", "teilbezahlt", "bezahlt", "ueberfaellig", "storniert"];

export const euro = (value?: number) => (value || 0).toLocaleString("de-DE", {
  style: "currency",
  currency: "EUR",
});

export const shortDate = (value?: string) => {
  if (!value) return "—";
  const parsed = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleDateString("de-DE");
};

/** Die Adresse des Beleg-PDFs — mit Fingerabdruck gegen den Browser-Cache.
 *
 * Ein geänderter Beleg behält seine Nummer, also auch seinen Dateinamen und
 * seine URL. Der Browser hielt die einmal geladene Fassung dann für gültig und
 * zeigte vor dem Versenden weiter das alte Dokument. `v` wechselt mit jedem
 * neu erzeugten PDF (`web_pdf_sha256`, ersatzweise `updated_at`) — damit ist
 * die neue Fassung eine andere Adresse. Das Backend schickt zusätzlich
 * `Cache-Control: no-store`; beides zusammen, weil PDFs auch außerhalb der App
 * geöffnet werden.
 */
export const invoicePdfUrl = (
  item: Pick<InvoiceRegisterItem, "id" | "web_pdf_sha256" | "updated_at">,
  optionen: { download?: boolean; viewer?: boolean } = {},
) => {
  const stand = (item.web_pdf_sha256 || item.updated_at || "").slice(0, 16) || "0";
  const frage = optionen.download ? `?download=true&v=${encodeURIComponent(stand)}` : `?v=${encodeURIComponent(stand)}`;
  const raute = optionen.viewer ? "#toolbar=0&navpanes=0&statusbar=0&view=FitH" : "";
  return `/api/kontor/rechnungen/${item.id}/pdf${frage}${raute}`;
};

const pdfStand = (item: Pick<InvoiceRegisterItem, "web_pdf_sha256" | "updated_at">) =>
  encodeURIComponent((item.web_pdf_sha256 || item.updated_at || "").slice(0, 16) || "0");

/** Eine Seite des Beleg-PDFs als Bild (für die kleine Vorschau im Belegdetail). */
export const invoiceSeiteUrl = (
  item: Pick<InvoiceRegisterItem, "id" | "web_pdf_sha256" | "updated_at">,
  seite: number,
  breite: number,
) => `/api/kontor/rechnungen/${item.id}/seite/${seite}?w=${breite}&v=${pdfStand(item)}`;

/**
 * Angebot oder Rechnung für die Dokumentansicht (`DokumentLightbox`) — als
 * Seitenbilder, die der Server rendert. Ein eingebettetes PDF zeigte das
 * iPhone nur mit der ersten Seite, Android gar nicht.
 */
export const invoiceDokument = (
  item: Pick<InvoiceRegisterItem, "id" | "web_pdf_sha256" | "updated_at" | "nummer" | "kind">,
  unterzeile?: string,
): NonNullable<Dokument> => ({
  titel: `${KIND_LABEL[item.kind] ?? "Beleg"} ${item.nummer || ""}`.trim(),
  unterzeile,
  url: invoicePdfUrl(item),
  download: invoicePdfUrl(item, { download: true }),
  seitenZahlUrl: `/api/kontor/rechnungen/${item.id}/seiten?v=${pdfStand(item)}`,
  seiteUrl: (seite, breite) => invoiceSeiteUrl(item, seite, breite),
});

export const statusTone = (status: InvoiceStatus) => {
  if (["bezahlt", "angenommen"].includes(status)) return "success";
  if (["ueberfaellig", "abgelehnt"].includes(status)) return "danger";
  if (["gestellt", "teilbezahlt"].includes(status)) return "warning";
  return "muted";
};

/** Rahmensegmente, die FastAPI jedem `loc` voranstellt — für den Nutzer Rauschen. */
const LOC_RAHMEN = new Set(["body", "query", "path", "header", "cookie", "draft"]);

/**
 * Listenfelder und wie eine ihrer Zeilen im Text heißen soll.
 *
 * Steht ein Feld hier drin, wird aus `["line_rows", 10]` „Position 11" statt
 * „line_rows · #11" — der Zähler ist einsbasiert, weil der Nutzer die Zeilen
 * im Editor genauso durchnummeriert sieht.
 */
const LOC_LISTEN: Record<string, string> = {
  line_rows: "Position",
  lines: "Position",
  positionen: "Position",
  abschlag_absetzungen: "Absetzung",
  anteile_prozent: "Anteil",
};

/** Feldnamen aus den Pydantic-Modellen in die Sprache der Oberfläche. */
const LOC_FELDER: Record<string, string> = {
  abschlag_absetzungen: "Abschläge absetzen",
  anteile_prozent: "Positionsanteile",
  brutto: "Brutto",
  customer_lines: "Rechnungsanschrift",
  customer_number: "Kunde",
  description: "Beschreibung",
  discount_amount: "Rabattbetrag",
  discount_percent: "Rabatt in Prozent",
  doc_number: "Belegnummer",
  due_date: "Fälligkeit",
  faellig_am: "Fälligkeit",
  intro_paragraphs: "Einleitungstext",
  issue_date: "Belegdatum",
  kind: "Belegart",
  kunde_id: "Kunde",
  leistungsdatum: "Leistungsdatum",
  line_rows: "Positionen",
  mwst: "Umsatzsteuer",
  netto: "Netto",
  notes_after_table: "Schlusstext",
  notiz: "Notiz",
  nummer: "Nummer",
  payment_reference: "Verwendungszweck",
  payment_term_days: "Zahlungsziel",
  projekt_id: "Projekt",
  quantity: "Menge",
  service_date: "Leistungsdatum",
  status: "Status",
  doc_title: "Titel",
  subject: "Betreff",
  title: "Bezeichnung",
  unit_label: "Einheit",
  unit_price_net: "Einzelpreis",
  validity_until: "Gültig bis",
  vat_rate_percent: "Umsatzsteuersatz",
};

/**
 * Aus dem `loc` einer Pydantic-Fehlerzeile eine Ortsangabe machen.
 *
 * `["body","draft","line_rows",10,"unit_price_net"]` → „Position 11 · Einzelpreis".
 * Unbekannte Segmente bleiben stehen, statt zu verschwinden — lieber ein
 * technischer Name als gar keine Spur zum Feld.
 */
const locPfad = (loc: unknown): string => {
  if (!Array.isArray(loc)) return "";
  const teile: string[] = [];
  const rest = loc.filter((seg, i) => !(i < 2 && typeof seg === "string" && LOC_RAHMEN.has(seg)));
  for (let i = 0; i < rest.length; i += 1) {
    const seg = rest[i];
    const naechstes = rest[i + 1];
    if (typeof seg === "string" && typeof naechstes === "number" && LOC_LISTEN[seg]) {
      teile.push(`${LOC_LISTEN[seg]} ${naechstes + 1}`);
      i += 1;
    } else if (typeof seg === "string") {
      teile.push(LOC_FELDER[seg] || seg);
    } else if (typeof seg === "number") {
      teile.push(`#${seg + 1}`);
    }
  }
  return teile.join(" · ");
};

/**
 * Die Pydantic-Meldung eindeutschen — über `type`, nicht über den Text.
 *
 * `msg` ist englischer Fliesstext und ändert sich zwischen Pydantic-Fassungen;
 * `type` plus `ctx` sind die stabilen Angaben. Was hier nicht steht, faellt auf
 * `msg` zurueck: eine englische Meldung ist unschoen, eine fehlende ist schlimmer.
 */
const locMeldung = (eintrag: Record<string, unknown>): string => {
  const typ = String(eintrag.type || "");
  const ctx = (eintrag.ctx || {}) as Record<string, unknown>;
  const roh = String(eintrag.msg || "").trim();
  switch (typ) {
    case "greater_than_equal": return `darf nicht kleiner als ${ctx.ge} sein`;
    case "greater_than": return `muss größer als ${ctx.gt} sein`;
    case "less_than_equal": return `darf höchstens ${ctx.le} sein`;
    case "less_than": return `muss kleiner als ${ctx.lt} sein`;
    case "string_too_short":
      return Number(ctx.min_length) <= 1
        ? "darf nicht leer sein"
        : `braucht mindestens ${ctx.min_length} Zeichen`;
    case "string_too_long": return `ist zu lang — höchstens ${ctx.max_length} Zeichen`;
    case "too_short": return `braucht mindestens ${ctx.min_length} Einträge`;
    case "too_long": return `darf höchstens ${ctx.max_length} Einträge haben`;
    case "missing": return "fehlt";
    case "literal_error": return `hat einen unzulässigen Wert (erlaubt: ${ctx.expected})`;
    case "value_error": return roh.replace(/^Value error,\s*/i, "");
    default: return roh;
  }
};

/**
 * Lesbare Fehlermeldung aus einer API-Antwort.
 *
 * FastAPI liefert `detail` je nach Fehlerart als Text (HTTPException) **oder**
 * als Liste von Objekten (422, Feldvalidierung). `String(detail)` machte aus
 * dem zweiten Fall „[object Object]" — der Nutzer sah einen Fehler, aber nicht
 * welchen.
 *
 * Seit 07.09.2026 wird bei der Listenform auch das **`loc`** ausgewertet, nicht
 * nur `msg`. Vorher las Volker bei einer gesperrten Abzugszeile bloss „Input
 * should be greater than or equal to 0" — bei elf Positionen im Beleg ohne jeden
 * Hinweis, welche gemeint war, und ohne Feldnamen. Jetzt steht dort
 * „Position 11 · Einzelpreis: darf nicht kleiner als 0 sein".
 *
 * Mehrere Fehler bleiben in einer Zeile, durch „ | " getrennt: der Balken über
 * dem Editor ist einzeilig, und mehr als drei Meldungen liest ohnehin niemand —
 * deshalb wird ab dem vierten nur noch gezaehlt.
 */
export const errorText = (error: unknown) => {
  const detail = (error && typeof error === "object" && "body" in error)
    ? (error as { body?: { detail?: unknown } }).body?.detail
    : undefined;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const zeilen = detail
      .map((d) => {
        if (!d || typeof d !== "object") return String(d);
        const eintrag = d as Record<string, unknown>;
        const wo = locPfad(eintrag.loc);
        const was = locMeldung(eintrag);
        if (!was) return wo;
        return wo ? `${wo}: ${was}` : was;
      })
      .filter(Boolean);
    if (zeilen.length > 3) {
      return `${zeilen.slice(0, 3).join(" | ")} (und ${zeilen.length - 3} weitere)`;
    }
    if (zeilen.length) return zeilen.join(" | ");
  }
  if (detail && typeof detail === "object") {
    const eintrag = detail as Record<string, unknown>;
    // Strukturierte Antwort mit Klartext daneben, z. B. Terminkonflikte (409).
    if (typeof eintrag.meldung === "string" && eintrag.meldung.trim()) return eintrag.meldung;
    if (eintrag.msg) {
      const wo = locPfad(eintrag.loc);
      const was = locMeldung(eintrag);
      return wo ? `${wo}: ${was}` : was;
    }
  }
  return error instanceof Error ? error.message : "Die Änderung konnte nicht gespeichert werden.";
};
